import React from 'react';
import { Link } from 'react-router-dom';
import { PlayCircleIcon } from '@heroicons/react/24/outline';
import { scrollToElement } from '../../utils/helpers';

const HeroSection: React.FC = () => {
  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-900 via-primary-800 to-success-800 overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"></div>
      </div>

      {/* Floating Sports Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 animate-bounce opacity-20">
          <div className="w-16 h-16 bg-warning-500 rounded-full flex items-center justify-center">
            <span className="text-2xl">⚽</span>
          </div>
        </div>
        <div className="absolute top-40 right-20 animate-bounce delay-1000 opacity-20">
          <div className="w-12 h-12 bg-success-500 rounded-full flex items-center justify-center">
            <span className="text-xl">🏸</span>
          </div>
        </div>
        <div className="absolute bottom-40 left-20 animate-bounce delay-2000 opacity-20">
          <div className="w-14 h-14 bg-primary-500 rounded-full flex items-center justify-center">
            <span className="text-xl">🏏</span>
          </div>
        </div>
        <div className="absolute bottom-20 right-10 animate-bounce delay-500 opacity-20">
          <div className="w-12 h-12 bg-warning-500 rounded-full flex items-center justify-center">
            <span className="text-xl">🏀</span>
          </div>
        </div>
      </div>

      <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto pt-24 sm:pt-32">
        {/* Main Content */}
        <div className="space-y-8 animate-fade-in">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
            <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">Registrations Open Now</span>
          </div>

          {/* Main Heading */}
          <div className="space-y-4">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="block text-white">Kreed</span>
              <span className="block bg-gradient-to-r from-warning-400 to-success-400 bg-clip-text text-transparent">
                Odisha
              </span>
            </h1>
            
            <p className="text-xl sm:text-2xl lg:text-3xl font-light text-gray-200 max-w-4xl mx-auto leading-relaxed">
              The Pathway to Sporting Excellence
            </p>
          </div>

          {/* Description */}
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Join Odisha's most ambitious sports development program. From grassroots to glory, 
            we're nurturing champions across all 314 blocks with world-class coaching and facilities.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 pt-4">
            <Link
              to="/apply"
              className="group bg-gradient-to-r from-warning-500 to-warning-600 hover:from-warning-600 hover:to-warning-700 text-white font-bold text-lg px-8 py-4 rounded-lg shadow-2xl transform transition duration-300 hover:scale-105 hover:shadow-warning-500/25 inline-flex items-center space-x-2"
            >
              <span>Apply Now</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>

            <button
              onClick={() => scrollToElement('about')}
              className="group bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold text-lg px-8 py-4 rounded-lg border border-white/30 hover:border-white/50 transform transition duration-300 hover:scale-105 inline-flex items-center space-x-2"
            >
              <PlayCircleIcon className="w-6 h-6 group-hover:scale-110 transition-transform" />
              <span>Learn More</span>
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 pt-12 max-w-4xl mx-auto">
            <div className="text-center space-y-2">
              <div className="text-3xl sm:text-4xl font-bold text-warning-400">314</div>
              <div className="text-sm sm:text-base text-gray-300">Blocks Covered</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-3xl sm:text-4xl font-bold text-success-400">10+</div>
              <div className="text-sm sm:text-base text-gray-300">Sports Disciplines</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-3xl sm:text-4xl font-bold text-primary-400">100%</div>
              <div className="text-sm sm:text-base text-gray-300">Free Training</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;